"use client";

import { useEffect, useRef } from "react";
import { X } from "lucide-react";

export interface Plant {
  id: string;
  commonName: string;
  scientificName: string;
  sunLevel: string;
  category: string;
  description?: string;
  imageUrl?: string;
  waterNeeds?: string;
  zones?: string[];
  careInstructions?: string;
  height?: string;
  width?: string;
  bloomTime?: string;
  bloomColor?: string;
  price?: number | null;
  shopifyVariantId?: string | null;
}

interface PlantDetailsModalProps {
  plant: Plant | null;
  isOpen: boolean;
  onClose: () => void;
  onToggleSelection: (plantId: string) => void;
  isSelected: boolean;
  primaryColor?: string;
  soldOut?: boolean;
}

export function PlantDetailsModal({
  plant,
  isOpen,
  onClose,
  onToggleSelection,
  isSelected,
  primaryColor = "#10b981",
  soldOut = false,
}: PlantDetailsModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  // Handle click outside
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
      onClose();
    }
  };

  if (!isOpen || !plant) return null;

  const formatSunLevel = (sunLevel: string) => {
    return sunLevel
      .replace(/_/g, " ")
      .replace("PART SUN PART SHADE", "Part Sun")
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const actionDisabled = soldOut && !isSelected;

  const handleToggle = () => {
    if (actionDisabled) return;
    onToggleSelection(plant.id);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fadeIn"
      onClick={handleBackdropClick}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />

      {/* Modal */}
      <div
        ref={modalRef}
        className="relative bg-white dark:bg-plantista-950 border border-gray-200 dark:border-plantista-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden animate-slideUp"
      >
        {/* Header with Image */}
        <div className="relative h-64 bg-gray-100 dark:bg-plantista-900">
          {plant.imageUrl ? (
            <img
              src={plant.imageUrl}
              alt={plant.commonName}
              className="w-full h-full object-cover"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = "none";
                const parent = target.parentElement;
                if (parent) {
                  parent.innerHTML = `
                    <div class="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 dark:from-plantista-900 dark:to-plantista-800">
                      <svg class="w-24 h-24 text-gray-400 dark:text-plantista-700" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clip-rule="evenodd" />
                      </svg>
                    </div>
                  `;
                }
              }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 dark:from-plantista-900 dark:to-plantista-800">
              <svg
                className="w-24 h-24 text-gray-400 dark:text-plantista-700"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          )}

          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 bg-white/90 dark:bg-plantista-900/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-white dark:hover:bg-plantista-800 transition-all hover:scale-110 border border-gray-200 dark:border-plantista-700"
            aria-label="Close modal"
          >
            <X className="w-5 h-5 text-gray-600 dark:text-plantista-400" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-16rem)] p-6">
          {/* Plant Names */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <h2 className="text-3xl font-bold text-plantista-950 dark:text-white font-display">
                {plant.commonName}
              </h2>
              {soldOut && (
                <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-400">
                  Sold out
                </span>
              )}
            </div>
            <p className="text-lg text-gray-500 dark:text-plantista-400 italic">
              {plant.scientificName}
            </p>
          </div>

          {/* Quick Facts Grid */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-gray-50 dark:bg-plantista-900 border border-gray-200 dark:border-plantista-800 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-1">
                <svg
                  className="w-5 h-5 text-yellow-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM4.332 8.027a6.012 6.012 0 011.912-2.706C6.512 5.73 6.974 6 7.5 6A1.5 1.5 0 019 7.5V8a2 2 0 004 0 2 2 0 011.523-1.943A5.977 5.977 0 0116 10c0 .34-.028.675-.083 1H15a2 2 0 00-2 2v2.197A5.973 5.973 0 0110 16v-2a2 2 0 00-2-2 2 2 0 01-2-2 2 2 0 00-1.668-1.973z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-sm font-medium text-gray-500 dark:text-plantista-400">
                  Sun Exposure
                </span>
              </div>
              <p className="text-plantista-950 dark:text-white font-semibold">
                {formatSunLevel(plant.sunLevel)}
              </p>
            </div>

            <div className="bg-gray-50 dark:bg-plantista-900 border border-gray-200 dark:border-plantista-800 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-1">
                <svg
                  className="w-5 h-5 text-emerald-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M3 5a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2h-2.22l.123.489.804.804A1 1 0 0113 18H7a1 1 0 01-.707-1.707l.804-.804L7.22 15H5a2 2 0 01-2-2V5zm5.771 7H5V5h10v7H8.771z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-sm font-medium text-gray-500 dark:text-plantista-400">
                  Category
                </span>
              </div>
              <p className="text-plantista-950 dark:text-white font-semibold capitalize">
                {plant.category}
              </p>
            </div>

            {plant.waterNeeds && (
              <div className="bg-gray-50 dark:bg-plantista-900 border border-gray-200 dark:border-plantista-800 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-1">
                  <svg
                    className="w-5 h-5 text-blue-500"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-sm font-medium text-gray-500 dark:text-plantista-400">
                    Water Needs
                  </span>
                </div>
                <p className="text-plantista-950 dark:text-white font-semibold capitalize">
                  {plant.waterNeeds}
                </p>
              </div>
            )}

            {plant.zones && plant.zones.length > 0 && (
              <div className="bg-gray-50 dark:bg-plantista-900 border border-gray-200 dark:border-plantista-800 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-1">
                  <svg
                    className="w-5 h-5 text-purple-500"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-sm font-medium text-gray-500 dark:text-plantista-400">
                    Hardiness Zones
                  </span>
                </div>
                <p className="text-plantista-950 dark:text-white font-semibold">
                  {plant.zones.join(", ")}
                </p>
              </div>
            )}

            {plant.height && (
              <div className="bg-gray-50 dark:bg-plantista-900 border border-gray-200 dark:border-plantista-800 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-1">
                  <svg
                    className="w-5 h-5 text-plantista-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
                    />
                  </svg>
                  <span className="text-sm font-medium text-gray-500 dark:text-plantista-400">
                    Height
                  </span>
                </div>
                <p className="text-plantista-950 dark:text-white font-semibold">
                  {plant.height}
                </p>
              </div>
            )}

            {plant.width && (
              <div className="bg-gray-50 dark:bg-plantista-900 border border-gray-200 dark:border-plantista-800 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-1">
                  <svg
                    className="w-5 h-5 text-plantista-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7h12M8 12h12m-12 5h12M4 7h.01M4 12h.01M4 17h.01"
                    />
                  </svg>
                  <span className="text-sm font-medium text-gray-500 dark:text-plantista-400">
                    Width
                  </span>
                </div>
                <p className="text-plantista-950 dark:text-white font-semibold">
                  {plant.width}
                </p>
              </div>
            )}

            {plant.bloomTime && (
              <div className="bg-gray-50 dark:bg-plantista-900 border border-gray-200 dark:border-plantista-800 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-1">
                  <svg
                    className="w-5 h-5 text-pink-500"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
                  </svg>
                  <span className="text-sm font-medium text-gray-500 dark:text-plantista-400">
                    Bloom Time
                  </span>
                </div>
                <p className="text-plantista-950 dark:text-white font-semibold capitalize">
                  {plant.bloomTime}
                </p>
              </div>
            )}

            {plant.bloomColor && (
              <div className="bg-gray-50 dark:bg-plantista-900 border border-gray-200 dark:border-plantista-800 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-1">
                  <svg
                    className="w-5 h-5 text-red-500"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4 2a2 2 0 00-2 2v11a3 3 0 106 0V4a2 2 0 00-2-2H4zm1 14a1 1 0 100-2 1 1 0 000 2zm5-1.757l4.9-4.9a2 2 0 000-2.828L13.485 5.1a2 2 0 00-2.828 0L10 5.757v8.486zM16 18H9.071l6-6H16a2 2 0 012 2v2a2 2 0 01-2 2z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-sm font-medium text-gray-500 dark:text-plantista-400">
                    Bloom Color
                  </span>
                </div>
                <p className="text-plantista-950 dark:text-white font-semibold capitalize">
                  {plant.bloomColor}
                </p>
              </div>
            )}
          </div>

          {/* Description */}
          {plant.description && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-plantista-950 dark:text-white mb-2">
                Description
              </h3>
              <p className="text-gray-600 dark:text-plantista-300 leading-relaxed">
                {plant.description}
              </p>
            </div>
          )}

          {/* Care Instructions */}
          {plant.careInstructions && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-plantista-950 dark:text-white mb-2">
                Care Instructions
              </h3>
              <p className="text-gray-600 dark:text-plantista-300 leading-relaxed whitespace-pre-line">
                {plant.careInstructions}
              </p>
            </div>
          )}
        </div>

        {/* Footer with Action Button */}
        <div className="border-t border-gray-200 dark:border-plantista-800 p-6 bg-gray-50 dark:bg-plantista-950">
          <button
            onClick={handleToggle}
            disabled={actionDisabled}
            aria-disabled={actionDisabled}
            className={`w-full py-3 px-6 rounded-lg font-semibold transition-all flex items-center justify-center space-x-2 ${
              actionDisabled
                ? "cursor-not-allowed opacity-60 bg-gray-200 dark:bg-plantista-900 border border-gray-300 dark:border-plantista-800"
                : "transform hover:scale-[1.02] active:scale-[0.98]"
            }`}
            style={
              actionDisabled
                ? undefined
                : {
                    backgroundColor: isSelected ? "#ef4444" : primaryColor,
                    color: "white",
                  }
            }
          >
            {actionDisabled ? (
              <span className="text-gray-500 dark:text-plantista-400">
                Sold out
              </span>
            ) : isSelected ? (
              <>
                <svg
                  className="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>Remove from Design</span>
              </>
            ) : (
              <>
                <svg
                  className="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>Add to Design</span>
              </>
            )}
          </button>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideUp {
          from {
            transform: translateY(20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }

        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
